import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/api'
import './CompletarPerfil.css'

function CompletarPerfil() {
    const { usuario, isLogado } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState('')

    const [formData, setFormData] = useState({
        cpfCnpj: usuario?.cpfCnpj || usuario?.cpf_cnpj || '',
        telefone: usuario?.telefone || '',
        whatsapp: usuario?.whatsapp || '',
        cidade: usuario?.cidade || '',
        estado: usuario?.estado || '',
        // Campos de vendedor
        nomeFazenda: '',
        cidadeFazenda: '',
        estadoFazenda: '',
        // Campos de motorista
        tipoVeiculo: '',
        placaVeiculo: '',
        capacidadePeso: '',
        capacidadeGado: ''
    })

    const hasDadosPessoais = !!(usuario?.cpfCnpj || usuario?.cpf_cnpj) && !!usuario?.telefone && !!usuario?.cidade && !!usuario?.estado;

    useEffect(() => {
        if (!isLogado) {
            navigate('/login')
            return
        }

        // Se perfil já está completo, redirecionar
        if (usuario?.perfilCompleto) {
            navigate('/perfil')
        }
    }, [isLogado, usuario, navigate])

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
        setErro('')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setErro('')

        try {
            const response = await authService.completarPerfil(formData)

            // Atualizar localStorage com novos dados
            localStorage.setItem('usuario', JSON.stringify(response.usuario))
            if (response.token) {
                localStorage.setItem('token', response.token)
            }

            // Interceptar fluxo para Motoristas (Painel Pago)
            if (usuario?.tipo === 'motorista') {
                try {
                    const tokenOriginal = localStorage.getItem('token');
                    const tokenPraUsar = response.token || tokenOriginal;
                    const resp = await fetch('http://localhost:3001/api/pagamentos/checkout-motorista', {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${tokenPraUsar}` }
                    });
                    const data = await resp.json();
                    if (data.url) {
                        window.location.href = data.url; // Redireciona pro CheckoutPIX
                        return;
                    }
                } catch (e) {
                    console.error("Erro no redirecionamento do checkout:", e);
                }
            }

            // Redirecionar para home se não for motorista
            navigate('/')
            window.location.reload()
        } catch (error) {
            setErro(error.message || 'Erro ao completar perfil')
        } finally {
            setLoading(false)
        }
    }

    const getTipoInfo = () => {
        switch (usuario?.tipo) {
            case 'comprador':
                return {
                    icon: '🛒',
                    titulo: 'Complete seu Perfil de Comprador',
                    desc: 'Complete seus dados para poder conversar com vendedores e fazer compras'
                }
            case 'vendedor':
                return {
                    icon: '🏷️',
                    titulo: 'Complete seu Perfil de Vendedor',
                    desc: 'Complete seus dados para poder criar anúncios e vender seu gado'
                }
            case 'motorista':
                return {
                    icon: '🚚',
                    titulo: 'Complete seu Perfil de Motorista',
                    desc: 'Complete seus dados para aparecer na lista de motoristas disponíveis'
                }
            default:
                return {
                    icon: '👤',
                    titulo: 'Complete seu Perfil',
                    desc: 'Complete seus dados para desbloquear todas as funcionalidades'
                }
        }
    }

    const tipoInfo = getTipoInfo()

    const estados = [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
        'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
        'SP', 'SE', 'TO'
    ]

    const tiposVeiculo = [
        'Caminhão Boiadeiro',
        'Carreta Boiadeira',
        'Caminhão Truck',
        'Bi-trem',
        'Rodotrem'
    ]

    return (
        <div className="completar-page">
            <div className="completar-container">
                <div className="completar-card">
                    <div className="completar-header">
                        <div className="completar-icon">{tipoInfo.icon}</div>
                        <h1>{tipoInfo.titulo}</h1>
                        <p>{tipoInfo.desc}</p>
                    </div>

                    <div className="verificado-badge">
                        <span className="badge-icon">🛡️</span>
                        <span>Após completar, você receberá o selo de <strong>Verificado</strong></span>
                    </div>

                    {erro && (
                        <div className="alert alert-error">
                            {erro}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="completar-form">
                        {/* Dados básicos - apenas se o usuário ainda não tiver preenchido */}
                        {!hasDadosPessoais && (
                            <div className="form-section">
                                <h3>📋 Dados Pessoais</h3>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">CPF ou CNPJ *</label>
                                        <input
                                            type="text"
                                            name="cpfCnpj"
                                            className="form-input"
                                            placeholder="000.000.000-00"
                                            value={formData.cpfCnpj}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Telefone *</label>
                                        <input
                                            type="tel"
                                            name="telefone"
                                            className="form-input"
                                            placeholder="(00) 00000-0000"
                                            value={formData.telefone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">WhatsApp (fechar negócio) *</label>
                                        <input
                                            type="tel"
                                            name="whatsapp"
                                            className="form-input"
                                            placeholder="(00) 00000-0000"
                                            value={formData.whatsapp}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Cidade *</label>
                                        <input
                                            type="text"
                                            name="cidade"
                                            className="form-input"
                                            placeholder="Sua cidade"
                                            value={formData.cidade}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Estado *</label>
                                        <select
                                            name="estado"
                                            className="form-input"
                                            value={formData.estado}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Selecione</option>
                                            {estados.map(uf => (
                                                <option key={uf} value={uf}>{uf}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Campos específicos - Vendedor */}
                        {usuario?.tipo === 'vendedor' && (
                            <div className="form-section">
                                <h3>🏠 Dados da Fazenda</h3>

                                <div className="form-group">
                                    <label className="form-label">Nome da Fazenda *</label>
                                    <input
                                        type="text"
                                        name="nomeFazenda"
                                        className="form-input"
                                        placeholder="Ex: Fazenda Bom Sucesso"
                                        value={formData.nomeFazenda}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Cidade da Fazenda</label>
                                        <input
                                            type="text"
                                            name="cidadeFazenda"
                                            className="form-input"
                                            placeholder="Cidade onde fica a fazenda"
                                            value={formData.cidadeFazenda}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Estado</label>
                                        <select
                                            name="estadoFazenda"
                                            className="form-input"
                                            value={formData.estadoFazenda}
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione</option>
                                            {estados.map(uf => (
                                                <option key={uf} value={uf}>{uf}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Campos específicos - Motorista */}
                        {usuario?.tipo === 'motorista' && (
                            <div className="form-section">
                                <h3>🚚 Dados do Veículo</h3>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Tipo de Veículo *</label>
                                        <select
                                            name="tipoVeiculo"
                                            className="form-input"
                                            value={formData.tipoVeiculo}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Selecione</option>
                                            {tiposVeiculo.map(tipo => (
                                                <option key={tipo} value={tipo}>{tipo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Placa do Veículo *</label>
                                        <input
                                            type="text"
                                            name="placaVeiculo"
                                            className="form-input"
                                            placeholder="ABC-1234"
                                            value={formData.placaVeiculo}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Capacidade de Peso (kg)</label>
                                        <input
                                            type="number"
                                            name="capacidadePeso"
                                            className="form-input"
                                            placeholder="Ex: 15000"
                                            value={formData.capacidadePeso}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Qtd. de Gado que Transporta</label>
                                        <input
                                            type="number"
                                            name="capacidadeGado"
                                            className="form-input"
                                            placeholder="Ex: 30"
                                            value={formData.capacidadeGado}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            className={`btn btn-primary btn-lg btn-block ${loading ? 'loading' : ''}`}
                            disabled={loading}
                        >
                            {loading ? 'Salvando...' : '✓ Completar Perfil e Verificar'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default CompletarPerfil
